import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { countryCodes, submitContactForm } from '../mock';
import { toast } from 'sonner';

export const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    handleChange('phone', value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.phone.length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitContactForm(formData);
      toast.success(response.message);
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        phone: '',
        message: ''
      });
      onClose();
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#EFEBE9] border-[#8D6E63]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#3E2723]">
            Register Your Trademark
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Input
              type="text"
              placeholder="Full Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              className="bg-white border-[#D7CCC8] focus:border-[#8D6E63] text-[#3E2723]"
            />
          </div>
          
          <div>
            <Input
              type="email"
              placeholder="Email Address *"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              className="bg-white border-[#D7CCC8] focus:border-[#8D6E63] text-[#3E2723]"
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
              <SelectTrigger className="w-32 bg-white border-[#D7CCC8] focus:border-[#8D6E63] text-[#3E2723]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#D7CCC8]">
                {countryCodes.map(({ code, country }) => (
                  <SelectItem key={code} value={code} className="text-[#3E2723]">
                    {code} {country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="tel"
              placeholder="Phone Number (10 digits) *"
              value={formData.phone}
              onChange={handlePhoneChange}
              required
              maxLength={10}
              className="flex-1 bg-white border-[#D7CCC8] focus:border-[#8D6E63] text-[#3E2723]"
            />
          </div>
          
          <div>
            <Textarea
              placeholder="Tell us about your trademark needs..."
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              rows={4}
              className="bg-white border-[#D7CCC8] focus:border-[#8D6E63] text-[#3E2723] resize-none"
            />
          </div>
          
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#3E2723] hover:bg-[#5D4037] text-white transition-colors duration-300"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
